import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, PhoneIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

const LINKS = {
  Pages: [
    { label: "Home",          to: "/" },
    { label: "About Us",      to: "/about" },
    { label: "Notice Board",  to: "/notice" },
    { label: "Gallery",       to: "/gallery" },
    { label: "Achievements",  to: "/achievements" },
    { label: "Articles",      to: "/articles" },
    { label: "FAQs",          to: "/faqs" },
    { label: "Contact Us",    to: "/contact" },
  ],
  Programs: [
    { label: "CEE Preparation",       to: "/info/cee" },
    { label: "IOE Engineering",        to: "/info/engineering" },
    { label: "Applied Science",        to: "/info/appliedScience" },
    { label: "CMAT Preparation",       to: "/info/cmat" },
    { label: "SEE Preparation",        to: "/info/see" },
    { label: "After SEE Entrance",     to: "/info/afterSEE" },
    { label: "Metropolitan Exam",      to: "/info/metropolitan" },
    { label: "Computer Classes",       to: "/programs/computer-classes" },
    { label: "Accounting Classes",     to: "/programs/accounting-classes" },
  ],
  Legal: [
    { label: "Privacy Policy",   to: "/privacy-policy" },
    { label: "Terms & Conditions", to: "/terms" },
    { label: "Register",         to: "/register" },
  ],
};

// Simple inline SVG social icons — no emoji, no external dependency.
// Anything that needs to punch a hole in the glyph paints the surface token
// rather than a literal white, so the icons survive the theme flip.
const SOCIAL = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/WhitehouseBridgeCourse",
    svg: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/whitehouseeducationzone",
    svg: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="3" />
        <circle cx="17.5" cy="6.5" r="0.7" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/@whitehouseeducationzonechi2971",
    svg: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.54 6.42a2.78 2.78 0 00-1.94-1.96C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.4 19.54C5.12 20 12 20 12 20s6.88 0 8.6-.46a2.78 2.78 0 001.94-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z" />
        <polygon fill="rgb(var(--bg))" points="9.75,15.02 15.5,12 9.75,8.98" />
      </svg>
    ),
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@whitehouseeducationzone",
    svg: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z" />
      </svg>
    ),
  },
];

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    // Quiet tinted well rather than a colour band, so it stays coherent with a
    // navbar that sits on the page ground. Both tokens flip with the theme.
    <footer className="bg-bg-subtle border-t border-subtle">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">

        {/* Top grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-14">

          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-5">
              {/* White plate: the logo's navy strokes would disappear on the dark theme. */}
              <span className="inline-flex items-center justify-center rounded-md bg-white p-1.5 border border-subtle">
                <img src="/img/logo.png" alt="Whitehouse Education Zone" className="h-9 w-auto" />
              </span>
              <div>
                <div className="text-fg font-bold text-[15px] leading-tight">Whitehouse</div>
                <div className="text-brand-text text-[10px] font-semibold tracking-[0.18em] uppercase mt-0.5">
                  Education Zone
                </div>
              </div>
            </Link>

            <p className="text-fg-muted text-xs leading-relaxed mb-6">
              Empowering students in Bharatpur, Nepal since 2068 B.S.<br />
              Your success is our mission — one student at a time.
            </p>

            <div className="flex gap-2">
              {SOCIAL.map(({ name, href, svg }) => (
                <a
                  key={name}
                  href={href}
                  aria-label={name}
                  className="w-8 h-8 rounded-lg bg-bg border border-subtle flex items-center justify-center text-fg-muted hover:text-primary-text hover:border-primary/40 transition-colors duration-fast ease-standard"
                >
                  {svg}
                </a>
              ))}
            </div>

            {/* Contact details */}
            <div className="mt-5 pt-4 border-t border-subtle space-y-2">
              {[
                { Icon: PhoneIcon,   text: "056-493090",             href: "tel:+97756493090"               },
                { Icon: PhoneIcon,   text: "9845210977",             href: "tel:+9779845210977"              },
                { Icon: PhoneIcon,   text: "9821285770",             href: "tel:+9779821285770"              },
                { Icon: EnvelopeIcon,text: "contact@whitehouse.com.np", href: "mailto:contact@whitehouse.com.np" },
              ].map(({ Icon, text, href }) => (
                <a key={text} href={href} className="flex items-center gap-2 text-fg-secondary hover:text-primary-text text-xs transition-colors duration-fast ease-standard">
                  <Icon className="w-3 h-3 flex-shrink-0 text-primary" />
                  <span>{text}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(LINKS).map(([group, items]) => (
            <div key={group}>
              <h4 className="text-fg font-bold text-xs uppercase tracking-[0.18em] mb-5">
                {group}
              </h4>
              <ul className="space-y-3 list-none p-0 m-0">
                {items.map(({ label, to }) => (
                  <li key={label}>
                    <Link
                      to={to}
                      className="group inline-flex items-center gap-1 text-fg-secondary hover:text-primary-text text-xs transition-colors duration-fast ease-standard"
                    >
                      {label}
                      <ArrowRightIcon className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-base ease-standard" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-subtle flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-fg-muted">
          <span>
            &copy; {year}{" "}
            <a
              href="https://www.whitehouse.com.np"
              className="hover:text-primary-text transition-colors duration-fast ease-standard"
            >
              Whitehouse Education Zone
            </a>
            . All rights reserved.
          </span>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-primary-text transition-colors duration-fast ease-standard">Privacy</Link>
            <Link to="/terms"          className="hover:text-primary-text transition-colors duration-fast ease-standard">Terms</Link>
            <span>Made with care in Nepal</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
